import {
  TurnstileWidget,
  type TurnstileWidgetHandle,
} from "@furistic/sdk-fe-ui-forms";
import { Check, Mail, Send } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";

/**
 * Demo request form for the Furistic platform landing. Submits same-origin to
 * `/api/demo-request` (worker shim) → gateway `/api/landing/intake`
 * (Turnstile + honeypot + rate limit) → notifications NEW_LANDING_SUBMISSION →
 * Novu → hello@furistic.co. The mailto link stays as a fallback.
 */

type SubmissionState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "sent" }
  | { kind: "error"; message: string };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INTAKE_PATH = "/api/demo-request";
const SOURCE = "platform";
const FALLBACK_EMAIL = "hello@furistic.co";

async function readErrorMessage(response: Response): Promise<string> {
  const payload = (await response.json().catch(() => null)) as {
    error?: { message?: unknown };
  } | null;
  const message = payload?.error?.message;
  if (typeof message === "string" && message.length > 0) return message;
  if (response.status === 429)
    return "Too many submissions. Please try again shortly.";
  return "Something went wrong. Please try again.";
}

export function DemoRequestForm() {
  const [state, setState] = useState<SubmissionState>({ kind: "idle" });
  const [token, setToken] = useState<string | undefined>();
  const widgetRef = useRef<TurnstileWidgetHandle | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") ?? "").trim();
    const email = String(form.get("email") ?? "").trim();
    if (!name || !EMAIL_PATTERN.test(email)) {
      setState({
        kind: "error",
        message: "Add your name and a valid email address.",
      });
      return;
    }

    setState({ kind: "submitting" });
    try {
      const response = await fetch(INTAKE_PATH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionType: "demo",
          name,
          email,
          company: String(form.get("company") ?? "").trim() || undefined,
          message: String(form.get("message") ?? "").trim() || undefined,
          source: SOURCE,
          ...(token ? { "cf-turnstile-response": token } : {}),
          _website: String(form.get("_website") ?? ""),
        }),
      });
      if (!response.ok) {
        setState({ kind: "error", message: await readErrorMessage(response) });
        widgetRef.current?.reset();
        setToken(undefined);
        return;
      }
      setState({ kind: "sent" });
    } catch {
      setState({
        kind: "error",
        message: "Network error. Check your connection and retry.",
      });
      widgetRef.current?.reset();
      setToken(undefined);
    }
  }

  if (state.kind === "sent") {
    return (
      <div
        className="p-10 md:w-3/5 flex flex-col items-center justify-center text-center"
        role="status"
      >
        <div className="w-16 h-16 bg-violet-100 dark:bg-violet-900/30 rounded-full flex items-center justify-center mb-6">
          <Check aria-hidden="true" className="h-8 w-8 text-violet-700" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">Message sent</h2>
        <p className="text-slate-300 mb-6">
          Thanks — we&apos;ll get back to you within 24 hours.
        </p>
        <a
          href={`mailto:${FALLBACK_EMAIL}`}
          className="inline-flex items-center gap-2 text-violet-400 hover:underline"
        >
          <Mail aria-hidden="true" className="h-4 w-4" />
          {FALLBACK_EMAIL}
        </a>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto text-left">
      <h2 className="text-2xl font-bold text-white mb-2">Talk to us</h2>
      <p className="text-slate-300 mb-6">
        Tell us which industry you&apos;re building for and we&apos;ll get the
        right people in the room.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Honeypot — hidden from humans, tempting for bots. */}
        <div
          aria-hidden="true"
          className="absolute -left-[9999px] h-px w-px overflow-hidden"
        >
          <label>
            Website
            <input
              type="text"
              name="_website"
              tabIndex={-1}
              autoComplete="off"
            />
          </label>
        </div>

        <div>
          <label
            htmlFor="contact-name"
            className="block text-sm font-medium text-slate-300 mb-1"
          >
            Name
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            autoComplete="name"
            className="w-full rounded-lg border border-gray-700 bg-black/40 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="contact-email"
              className="block text-sm font-medium text-slate-300 mb-1"
            >
              Email
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              className="w-full rounded-lg border border-gray-700 bg-black/40 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div>
            <label
              htmlFor="contact-company"
              className="block text-sm font-medium text-slate-300 mb-1"
            >
              Company <span className="text-slate-500">(optional)</span>
            </label>
            <input
              id="contact-company"
              name="company"
              type="text"
              autoComplete="organization"
              className="w-full rounded-lg border border-gray-700 bg-black/40 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="contact-message"
            className="block text-sm font-medium text-slate-300 mb-1"
          >
            Message <span className="text-slate-500">(optional)</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            rows={4}
            className="w-full rounded-lg border border-gray-700 bg-black/40 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>

        <TurnstileWidget
          ref={widgetRef}
          siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
          onToken={setToken}
          onExpire={() => setToken(undefined)}
          onError={(code) =>
            setState({
              kind: "error",
              message:
                code === "missing-site-key"
                  ? "Security check unavailable — please email us directly."
                  : "Security check failed — please try again.",
            })
          }
          appearance="always"
        />

        {state.kind === "error" && (
          <p role="alert" className="text-sm text-red-400">
            {state.message}
          </p>
        )}

        <button
          type="submit"
          disabled={state.kind === "submitting"}
          className="w-full inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white font-bold py-3.5 px-8 rounded-lg transition transform active:scale-95"
        >
          <Send aria-hidden="true" className="h-4 w-4" />
          {state.kind === "submitting" ? "Sending…" : "Send"}
        </button>

        <p className="text-sm text-slate-400">
          Prefer email?{" "}
          <a href={`mailto:${FALLBACK_EMAIL}`} className="hover:underline">
            {FALLBACK_EMAIL}
          </a>
        </p>
      </form>
    </div>
  );
}
